<script setup lang="ts">
import type { Schemas } from '#shopware'

interface SidenavMenuProps {
    showCloseButton?: boolean
}

const props = withDefaults(defineProps<SidenavMenuProps>(), {
    showCloseButton: true,
})

const { isOpen, open, close } = useSidenav()
const mobileMenuOpen = isOpen('menu')
const navigationItems = inject('mainNavigation') as Schemas["NavigationRouteResponse"]

const route = useRoute()

// Get current category path segments from route
const currentCategorySegments = computed(() => {
    // Check for dynamic route params (catch-all routes)
    if (route.params.dynamic && Array.isArray(route.params.dynamic) && route.params.dynamic.length > 0) {
        return route.params.dynamic as string[]
    }
    return []
})

// Find the current active category based on full path segments
const findActiveCategoryPathBySegments = (
    items: Schemas["Category"][],
    segments: string[],
    currentPath: Schemas["Category"][] = [],
    segmentIndex: number = 0
): Schemas["Category"][] | null => {
    if (segmentIndex >= segments.length) return null

    const currentSegment = segments[segmentIndex]

    for (const item of items) {
        // Match by ID or by SEO URL path segment
        const itemSeoUrl = item.seoUrls?.[0]?.seoPathInfo || ''
        const itemSlug = itemSeoUrl.split('/').pop() || ''

        const isMatch = item.id === currentSegment ||
                       itemSlug === currentSegment ||
                       item.name?.toLowerCase() === currentSegment?.toLowerCase()

        if (isMatch) {
            const newPath = [...currentPath, item]

            // If this is the last segment, we found the complete path
            if (segmentIndex === segments.length - 1) {
                return newPath
            }

            // Otherwise, continue searching in children
            if (item.children && item.children.length > 0) {
                const found = findActiveCategoryPathBySegments(
                    item.children,
                    segments,
                    newPath,
                    segmentIndex + 1
                )
                if (found) return found
            }
        }
    }
    return null
}

const activeCategoryPath = computed(() => {
    if (!currentCategorySegments.value.length || !navigationItems?.length) {
        return []
    }

    const path = findActiveCategoryPathBySegments(
        navigationItems,
        currentCategorySegments.value
    ) || []

    return path
})
</script>

<template>
    <FoundationButton
        size="medium"
        variant="ghost"
        class="shrink-0 w-12 order-10 xl:hidden"
        square
        :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
        @click="open('menu')"
    >
        <span class="sr-only">{{ $t('sidenav.menu.mobile.open') }}</span><FoundationIcon name="menu" />
    </FoundationButton>

    <SidenavOverlay 
        v-model="mobileMenuOpen" 
        class="bg-white" 
        width-class="w-full lg:w-[600px]"
    >
        <nav 
            class="flex flex-col h-full" 
            :aria-label="$t('sidenav.menu.mobile.description')"
        >
            <!-- Header -->
            <SidenavHeader
                :title=" $t('sidenav.menu.mobile.title')"
                :show-close-button="props.showCloseButton"
                :close-aria-label="$t('cart.close')"
                @close="mobileMenuOpen = false"
            />

            <!-- Menu items -->
            <div class="flex flex-col flex-grow relative">
                <LazySidenavMenuRecursive
                    v-if="mobileMenuOpen"
                    :key="`${mobileMenuOpen}-${route.path}`"
                    :items="navigationItems"
                    :active-category-path="activeCategoryPath"
                    @navigate="close('menu')"
                />
            </div>
        </nav>
    </SidenavOverlay>
</template>
